/**
 * گزارشِ «این منبع را امتحان کن» — شکلش، و اینکه هر قدم قبول است یا نه.
 *
 * ═══ تقسیمِ کار با پایتون ═══
 *
 * کرالر فقط **واقعیت** می‌نویسد: چند بایت، چه کدی، چند آیتم، چه فیلدهایی.
 * هیچ‌جای گزارش «قبول/مردود» نمی‌نویسد. حکم این‌جاست، و این تصادفی نیست:
 *
 *   • حکم چیزی است که مدام عوض می‌شود («زیرِ سه آیتم هم مشکوک است»،
 *     «صفحه‌ی زیرِ دو کیلوبایت یعنی دیوارِ ضدربات»). اگر در پایتون بود،
 *     هر بار عوض کردنش یعنی یک استقرارِ کرالر.
 *   • این‌طوری حکم با `npm test` آزمودنی است — بی‌شبکه، بی‌دیتابیس.
 *   • و گزارشِ ذخیره‌شده‌ی دیروز با قاعده‌ی امروز دوباره خوانده می‌شود، نه
 *     با حکمی که آن شب منجمد شده.
 *
 * پس این فایل خالص است و هیچ ایمپورتی ندارد، مثلِ `sourcePipeline.ts`.
 */

/** شماره‌ی شکلِ گزارش. کرالر همین را می‌نویسد؛ نخواندنش یعنی گزارشِ ناشناخته. */
export const REPORT_VERSION = 1;

export type StepKey = 'fetch' | 'paginate' | 'detect' | 'extract' | 'origin' | 'detail';

/** ترتیبِ خواندن — همان ترتیبی که خودِ خطِ لوله طی می‌کند. */
export const STEP_ORDER: StepKey[] = [
  'fetch', 'paginate', 'detect', 'extract', 'origin', 'detail',
];

export const STEP_LABEL: Record<StepKey, string> = {
  fetch: 'گرفتنِ صفحه',
  paginate: 'صفحه‌بندی',
  detect: 'پیدا کردنِ آیتم‌ها',
  extract: 'استخراجِ فیلدها',
  origin: 'منبعِ رخداد',
  detail: 'صفحه‌ی خودِ رخداد',
};

/**
 * ⚠️ `skip` با `fail` یکی نیست و یکی نشان دادنشان کلِ ابزار را بی‌معنی
 * می‌کند: منبعی که صفحه‌بندی **ندارد** در قدمِ ۲ شکست نخورده — آن قدم
 * برایش موضوعیت ندارد. اولی خبر است، دومی کار.
 */
export type StepStatus = 'ok' | 'warn' | 'fail' | 'skip';

export const STATUS_LABEL: Record<StepStatus, string> = {
  ok: 'درست',
  warn: 'مشکوک',
  fail: 'خراب',
  skip: 'موضوعیت ندارد',
};

export const STATUS_ICON: Record<StepStatus, string> = {
  ok: '✅', warn: '⚠️', fail: '❌', skip: '—',
};

// ═══════════ واقعیت‌هایی که کرالر می‌نویسد ═══════════

/** یک درخواستِ شبکه، هر جای گزارش که باشد. */
export interface FetchFacts {
  /**
   * کدام قدم این درخواست را زد.
   *
   * ⚠ این‌جا برای نمایش نیست: کرالر گزارش را **قبل از** درجِ صفحه‌ها
   * می‌سازد و شناسه‌ی ردیفِ صفحه تا آن لحظه وجود ندارد، پس
   * `storage/source_tests.finish` از روی همین `role` است که `page_id` را
   * پُر می‌کند.
   */
  role: 'listing_1' | 'listing_2' | 'detail' | 'origin';
  url: string;
  /** بعد از ریدایرکت. `null` یعنی جابه‌جا نشد. */
  final_url: string | null;
  outcome: 'fetched' | 'blocked' | 'error';
  http_status: number | null;
  /** حجمِ واقعیِ دریافتی، حتی وقتی بدنه‌ی ذخیره‌شده بریده شده باشد. */
  bytes: number;
  duration_ms: number;
  content_type: string | null;
  /**
   * `true` اجازه داد · `false` بست · **`null` اصلاً پرسیده نشد**.
   *
   * ⚠️ سه‌حالته است و باید باشد: وقتی گرفتن پیش از هر درخواستی متوقف
   * می‌شود (منبعِ خاموش، یا تیرِ `disallowed`)، robots هیچ‌وقت خوانده
   * نمی‌شود. نوشتنِ «اجازه داد» در آن حالت ادعایی است که کسی نسنجیده.
   */
  robots_allowed: boolean | null;
  /**
   * نشانه‌ی دیوارِ ضدربات، یا `null`.
   *
   * ⚠️ عمداً رشته است و نه boolean: «کدام نشانه دیده شد» (`cf-challenge`،
   * `captcha`، `just-a-moment`) تنها چیزی است که می‌گوید کارِ بعدی چیست.
   */
  bot_wall: string | null;
  /** ردیفِ `crawler_source_test_pages` که بدنه‌اش آن‌جاست. */
  page_id: number | null;
  detail: string | null;
}

/** یک آیتمِ پیداشده در صفحه — همان `EventCandidate`، بریده برای نمایش. */
export interface ItemSample {
  key: string;
  locator: string | null;
  ordinal: number;
  links: string[];
  snippet: string;
  snippet_truncated: boolean;
}

/** فیلدهای استخراج‌شده از یک آیتم. */
export interface FieldSample {
  item_key: string;
  /** ‏۱ یا ۲ برای صفحه‌ی فهرست، و `'detail'` برای صفحه‌ی خودِ رخداد. */
  page: 1 | 2 | 'detail';
  fields: {
    field: string;
    value: unknown;
    extractor: string;
    confidence: number | null;
  }[];
}

export interface FetchStepFacts { page1: FetchFacts }

export interface PaginateStepFacts {
  /** منبع اصلاً قاعده‌ی صفحه‌بندی دارد؟ */
  configured: boolean;
  rule: Record<string, unknown> | null;
  page2: FetchFacts | null;
  /** بدنه‌ی صفحه‌ی دوم مو‌به‌مو همان صفحه‌ی اول است؟ */
  same_body: boolean | null;
  items_page1: number | null;
  items_page2: number | null;
  /** چند تا از آیتم‌های صفحه‌ی دوم در صفحه‌ی اول هم بودند. */
  repeated: number | null;
  /** نشانه‌ی «هنوز صفحه هست» در صفحه‌ی دوم دیده شد؟ `null` یعنی قاعده ندارد. */
  stop_marker_present: boolean | null;
}

export interface DetectStepFacts {
  detector: string;
  page1_items: number;
  page2_items: number | null;
  samples: ItemSample[];
  samples_page2: ItemSample[];
}

export interface ExtractStepFacts {
  extractor: string;
  page1_with_fields: number;
  page2_with_fields: number | null;
  samples: FieldSample[];

  /**
   * همه‌ی خواننده‌های عمومی روی همان بایت‌ها.
   *
   * ⚠️ هزینه‌اش صفر است — صفحه یک بار گرفته شده و این‌ها فقط پردازشِ
   * همان بایت‌هاست. دلیلِ وجودش این است که هر پیش‌نویس با `jsonld`
   * ساخته می‌شود (مقدارِ ثابتِ کدشده)، پس برای هر سایتی که JSON-LD
   * ندارد تنظیمِ اولیه از پیش غلط است.
   */
  tried?: { extractor: string; with_fields: number; with_essential: number;
            current: boolean }[];
  /** خواننده‌ای که واقعاً بهتر از تنظیمِ فعلی است، یا `null`. */
  best?: string | null;
  best_essential?: number | null;
}

export interface OriginStepFacts {
  /** مقدارِ `goout_source` منبع — انتسابی که در اپ دیده می‌شود. */
  goout_source: string | null;
  /** منبع aggregator است؟ فقط برای این‌ها «نداشتنِ origin» بد است. */
  aggregator: boolean;
  candidates: { url: string; score: number; anchor: string | null }[];
  chosen: string | null;
  fetched: FetchFacts | null;
}

export interface DetailStepFacts {
  url: string | null;
  /** چرا صفحه‌ی جزئیات نبود — وقتی `url` خالی است. */
  reason: string | null;
  fetched: FetchFacts | null;
  fields: FieldSample | null;
  /** فیلدهایی که فقط صفحه‌ی جزئیات داد و کارتِ فهرست نداشت. */
  extra_fields: string[];
  /**
   * صفحه‌ی خودِ رخداد **به‌تنهایی** عنوان و زمان می‌دهد؟
   *
   * ⚠️ این تک‌فیلد یک تشخیصِ کامل را عوض می‌کند: کارتِ بی‌فیلد وقتی مهم
   * نیست که صفحه‌ی رخداد کامل باشد — خطِ لوله ادعاهای فهرست و جزئیات را
   * با هم جمع می‌کند، پس منبع قابلِ استفاده است.
   */
  essential: boolean;
}

export interface SourceTestReport {
  version: number;
  source: {
    id: string;
    label: string | null;
    extractor: string | null;
    tier: string | null;
    timezone: string | null;
    urls: string[];
    aggregator: boolean;
  };
  steps: {
    fetch?: FetchStepFacts;
    paginate?: PaginateStepFacts;
    detect?: DetectStepFacts;
    extract?: ExtractStepFacts;
    origin?: OriginStepFacts;
    detail?: DetailStepFacts;
  };
  /** یک جمله که کرالر می‌نویسد؛ در فهرستِ اجراها دیده می‌شود. */
  summary?: string;
}

/** یک اجرا، همان‌طور که `admin_source_test_runs` برمی‌گرداند. */
export interface SourceTestRun {
  id: number;
  job_id: number | null;
  target: string;
  city: string | null;
  bare: string | null;
  settings_from: 'registry' | 'draft' | 'form';
  status: 'queued' | 'running' | 'done' | 'failed';
  error: string | null;
  requested_at: string;
  started_at: string | null;
  finished_at: string | null;
  summary?: string | null;
}

export interface SourceTestPage {
  id: number;
  role: 'listing_1' | 'listing_2' | 'detail' | 'origin';
  url: string;
  http_status: number | null;
  content_type: string | null;
  bytes: number;
  duration_ms: number;
  truncated: boolean;
  fetched_at: string;
  has_body: boolean;
}

export const PAGE_ROLE_LABEL: Record<SourceTestPage['role'], string> = {
  listing_1: 'صفحه‌ی اولِ فهرست',
  listing_2: 'صفحه‌ی دومِ فهرست',
  detail: 'صفحه‌ی رخداد',
  origin: 'صفحه‌ی منبع',
};

// ═══════════ حکم ═══════════

/**
 * صفحه‌ای که «آمد» ولی هیچ‌چیز نیست.
 *
 * ⚠️ عددش از یک موردِ واقعی آمد نه از سلیقه: صفحه‌ی چالشِ Cloudflare
 * کدِ ۲۰۰ می‌دهد و حدودِ یک کیلوبایت است، پس در هر گزارشی «موفق» به نظر
 * می‌رسد تا وقتی کسی حجمش را نگاه کند. این آستانه همان نگاه است.
 */
export const THIN_PAGE_BYTES = 2048;

/** زیرِ این تعداد آیتم، صفحه‌ی فهرست معمولاً فهرست نیست. */
export const FEW_ITEMS = 3;

/** بدونِ این‌ها یک رخداد رخداد نیست — هرچقدر فیلدِ دیگر داشته باشد. */
export const ESSENTIAL_FIELDS = ['title', 'starts_at'];

export interface StepVerdict {
  key: StepKey;
  status: StepStatus;
  /** یک جمله، فارسی، با عددهایی که تصمیم را ساخته‌اند. */
  note: string;
}

const bytesLabel = (n: number): string => {
  if (n < 1024) return `${n} بایت`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} کیلوبایت`;
  return `${(n / (1024 * 1024)).toFixed(2)} مگابایت`;
};

/** حجم، برای نمایش. جدا شده چون هم در حکم و هم در رابط لازم است. */
export const humanBytes = bytesLabel;

/** حکمِ یک درخواستِ شبکه — پایه‌ی قدم‌های ۱ و ۶ و صفحه‌ی دوم. */
function fetchVerdict(f: FetchFacts): { status: StepStatus; note: string } {
  // `null` (پرسیده نشد) نباید مثلِ `false` (بست) خوانده شود — دلیلِ
  // واقعی در `detail` است و بندِ بعدی می‌گویدش.
  if (f.robots_allowed === false) {
    return { status: 'fail', note: 'robots.txt این آدرس را بسته است.' };
  }
  if (f.outcome === 'blocked' && f.detail === 'enabled=false') {
    return {
      status: 'fail',
      note: 'منبع خاموش است، پس اصلاً درخواستی فرستاده نشد. اول روشنش کن.',
    };
  }
  if (f.outcome === 'blocked' && f.detail === 'tier=disallowed') {
    return {
      status: 'fail',
      note: 'تیرِ این منبع `disallowed` است — عمداً بسته شده و کرالر سراغش نمی‌رود.',
    };
  }
  if (f.outcome === 'blocked') {
    return { status: 'fail', note: `گرفته نشد — ${f.detail ?? 'بسته'}` };
  }
  if (f.outcome === 'error') {
    return {
      status: 'fail',
      note: `خطا${f.http_status ? ` (HTTP ${f.http_status})` : ''} — ${f.detail ?? 'بی‌جزئیات'}`,
    };
  }
  if (f.bot_wall) {
    return {
      status: 'fail',
      note: `دیوارِ ضدربات خورد (${f.bot_wall}) — ${bytesLabel(f.bytes)} در ${f.duration_ms} میلی‌ثانیه.`,
    };
  }
  if (f.bytes < THIN_PAGE_BYTES) {
    return {
      status: 'warn',
      note: `آمد ولی فقط ${bytesLabel(f.bytes)} — برای یک صفحه‌ی فهرست مشکوک است.`,
    };
  }
  const moved = f.final_url && f.final_url !== f.url ? ' (ریدایرکت شد)' : '';
  return {
    status: 'ok',
    note: `HTTP ${f.http_status} · ${bytesLabel(f.bytes)} · ${f.duration_ms} میلی‌ثانیه${moved}`,
  };
}

function paginateVerdict(p: PaginateStepFacts): { status: StepStatus; note: string } {
  if (!p.configured) {
    return { status: 'skip', note: 'این منبع قاعده‌ی صفحه‌بندی ندارد.' };
  }
  if (!p.page2) {
    return { status: 'fail', note: 'قاعده هست ولی صفحه‌ی دوم اصلاً ساخته نشد.' };
  }
  const got = fetchVerdict(p.page2);
  if (got.status === 'fail') {
    return { status: 'fail', note: `صفحه‌ی دوم ${got.note}` };
  }
  /**
   * ⚠️ مهم‌ترین حکمِ این فایل. سایتی که پارامترِ صفحه را نادیده می‌گیرد،
   * ‏۲۰۰ و بدنه‌ی سالم برمی‌گرداند — یعنی هر شب همان صفحه دوباره خوانده
   * می‌شود و هیچ آماری آن را لو نمی‌دهد.
   */
  if (p.same_body === true) {
    return {
      status: 'fail',
      note: 'صفحه‌ی دوم مو‌به‌مو همان صفحه‌ی اول است — پارامترِ صفحه کار نمی‌کند.',
    };
  }
  if (p.items_page2 === 0) {
    return {
      status: 'warn',
      note: 'صفحه‌ی دوم آمد ولی هیچ آیتمی نداشت — شاید فهرست همین یک صفحه است.',
    };
  }
  if (p.repeated != null && p.items_page2 != null && p.items_page2 > 0
      && p.repeated === p.items_page2) {
    return {
      status: 'fail',
      note: `هر ${p.items_page2} آیتمِ صفحه‌ی دوم در صفحه‌ی اول هم بود — صفحه جلو نرفته.`,
    };
  }
  const dup = p.repeated ? ` (${p.repeated} تا تکراری)` : '';
  // ⚠️ نبودِ نشانه یعنی کرالر بعد از همین صفحه می‌ایستد. عیب نیست — قاعده
  // همین است — ولی نگفتنش یعنی «۵ صفحه» در تنظیمات و ۲ صفحه در عمل، بی
  // اینکه جایی گفته شود.
  const stop = p.stop_marker_present === false
    ? ' · نشانه‌ی ادامه در صفحه‌ی دوم نبود، پس کرالر همان‌جا می‌ایستد'
    : '';
  return {
    status: 'ok',
    note: `صفحه‌ی دوم ${p.items_page2} آیتم داد${dup} · ${bytesLabel(p.page2.bytes)}${stop}`,
  };
}

function detectVerdict(d: DetectStepFacts): { status: StepStatus; note: string } {
  if (d.page1_items === 0) {
    return {
      status: 'fail',
      note: 'هیچ آیتمی در صفحه پیدا نشد — یا الگو عوض شده یا این صفحه فهرست نیست.',
    };
  }
  const second = d.page2_items == null ? '' : ` · صفحه‌ی دوم ${d.page2_items} تا`;
  if (d.page1_items < FEW_ITEMS) {
    return {
      status: 'warn',
      note: `فقط ${d.page1_items} آیتم${second} — برای یک فهرست کم است.`,
    };
  }
  return { status: 'ok', note: `${d.page1_items} آیتم${second}` };
}

/** فیلدهایی که در یک نمونه واقعاً مقدار دارند. */
function filled(sample: FieldSample): Set<string> {
  const names = new Set<string>();
  for (const f of sample.fields) {
    if (f.value !== null && f.value !== undefined && f.value !== '') names.add(f.field);
  }
  return names;
}

function extractVerdict(
  e: ExtractStepFacts,
  p: PaginateStepFacts | undefined,
): { status: StepStatus; note: string } {
  if (e.samples.length === 0 || e.page1_with_fields === 0) {
    return {
      status: 'fail',
      note: `آیتم‌ها پیدا شدند ولی «${e.extractor}» هیچ فیلدی از آن‌ها درنیاورد.`,
    };
  }
  // فقط نمونه‌های صفحه‌ی اول معیارِ «قابلِ استخراج بودن»اند؛ صفحه‌ی دوم
  // جمله‌ی خودش را دارد، پایین‌تر.
  const firstPage = e.samples.filter((s) => s.page === 1);
  const missing = ESSENTIAL_FIELDS.filter(
    (name) => !firstPage.some((s) => filled(s).has(name)),
  );
  if (missing.length > 0) {
    return {
      status: 'fail',
      note: `هیچ نمونه‌ای ${missing.join(' و ')} ندارد — بدونِ این‌ها رخداد ساخته نمی‌شود.`,
    };
  }

  /**
   * ⚠️ صفحه‌ی دومی که آیتم دارد ولی فیلد نمی‌دهد.
   *
   * این حالت را هیچ آماری لو نمی‌دهد و در اولین منبعی که این ابزار رویش
   * دوید پیدا شد: صفحه‌ی دوم ۳۸ آیتم داشت و **صفر** تایشان فیلد داد،
   * چون قالبِ صفحه‌ی دوم با اولی فرق دارد. یعنی صفحه‌بندی هر شب دانلود
   * می‌شود و هیچ رخدادی از آن درنمی‌آید. جمله‌ی جدا دارد چون از «یکی از
   * نمونه‌ها ناقص است» خیلی مهم‌تر است.
   */
  if (p?.configured && (p.items_page2 ?? 0) > 0 && e.page2_with_fields === 0) {
    return {
      status: 'warn',
      note: `صفحه‌ی اول درست است (${e.page1_with_fields} آیتم)، ولی صفحه‌ی دوم `
        + `${p.items_page2} آیتم داشت و هیچ‌کدام فیلدی ندادند — قالبش فرق دارد.`,
    };
  }

  const weak = firstPage.filter(
    (s) => !ESSENTIAL_FIELDS.every((name) => filled(s).has(name)),
  ).length;
  const second = e.page2_with_fields == null
    ? ''
    : ` · صفحه‌ی دوم ${e.page2_with_fields} آیتم`;
  if (weak > 0) {
    return {
      status: 'warn',
      note: `${e.page1_with_fields} آیتم فیلد داد${second}، ولی ${weak} نمونه از `
        + `${firstPage.length} تا عنوان یا زمان ندارد.`,
    };
  }
  return { status: 'ok', note: `${e.page1_with_fields} آیتم با فیلدِ کامل${second}` };
}

function originVerdict(o: OriginStepFacts): { status: StepStatus; note: string } {
  if (o.chosen) {
    const got = o.fetched ? fetchVerdict(o.fetched) : null;
    if (got && got.status === 'fail') {
      return { status: 'warn', note: `منبع پیدا شد ولی گرفته نشد — ${got.note}` };
    }
    return { status: 'ok', note: `منبع: ${o.chosen}` };
  }
  /**
   * ⚠️ نبودِ منبع فقط برای aggregator بد است. سایتِ خودِ سالن **خودش**
   * منبع است و لینکِ بیرونی نداشتنش عیب نیست — یکی کردنِ این دو یعنی
   * نصفِ منابعِ سالم هر بار زردِ بی‌دلیل می‌گیرند.
   */
  if (o.aggregator) {
    return {
      status: 'warn',
      note: 'این منبع aggregator است ولی هیچ لینکِ بیرونی‌ای در آیتم پیدا نشد.',
    };
  }
  const attributed = o.goout_source ? ` انتساب: ${o.goout_source}` : '';
  return { status: 'skip', note: `منبعِ بیرونی ندارد — خودش منبع است.${attributed}` };
}

function detailVerdict(d: DetailStepFacts): { status: StepStatus; note: string } {
  if (!d.url) {
    return { status: 'skip', note: d.reason ?? 'آیتم صفحه‌ی جداگانه‌ای ندارد.' };
  }
  if (!d.fetched) {
    return { status: 'fail', note: 'آدرسِ صفحه پیدا شد ولی گرفته نشد.' };
  }
  const got = fetchVerdict(d.fetched);
  if (got.status === 'fail') return { status: 'fail', note: got.note };

  if (d.extra_fields.length > 0) {
    return {
      status: 'ok',
      note: `${bytesLabel(d.fetched.bytes)} · ${d.extra_fields.length} فیلدِ بیشتر از کارت `
        + `(${d.extra_fields.join('، ')})`,
    };
  }
  return {
    status: 'warn',
    note: `${bytesLabel(d.fetched.bytes)} گرفته شد ولی چیزی بیشتر از کارتِ فهرست نداد.`,
  };
}

/**
 * حکمِ هر شش قدم.
 *
 * ⚠️ قدمی که در گزارش **نیست** با قدمی که `skip` شده یکی نیست: اولی یعنی
 * اجرا قبل از رسیدن به آن‌جا مرد (یا هنوز نرسیده)، دومی یعنی رسید و
 * موضوعیت نداشت. اولی `null` می‌گیرد و رابط خاکستری نشانش می‌دهد.
 */
export function verdicts(report: SourceTestReport | null): (StepVerdict | null)[] {
  const steps = report?.steps ?? {};
  return STEP_ORDER.map((key): StepVerdict | null => {
    switch (key) {
      case 'fetch':
        return steps.fetch
          ? { key, ...fetchVerdict(steps.fetch.page1) }
          : null;
      case 'paginate':
        return steps.paginate ? { key, ...paginateVerdict(steps.paginate) } : null;
      case 'detect':
        return steps.detect ? { key, ...detectVerdict(steps.detect) } : null;
      case 'extract':
        return steps.extract
          ? { key, ...extractVerdict(steps.extract, steps.paginate) }
          : null;
      case 'origin':
        return steps.origin ? { key, ...originVerdict(steps.origin) } : null;
      case 'detail':
        return steps.detail ? { key, ...detailVerdict(steps.detail) } : null;
      default:
        return null;
    }
  });
}

/**
 * یک جمله برای کلِ اجرا.
 *
 * ⚠️ «اولین قدمی که خراب است» و نه «چند تا خراب است»: وقتی گرفتنِ صفحه
 * شکست بخورد، هر پنج قدمِ بعدی هم خراب‌اند و شمردنشان فقط عددِ ترسناکی
 * می‌سازد که یک مشکل را پنج تا نشان می‌دهد.
 */
export function overall(report: SourceTestReport | null): {
  status: StepStatus;
  note: string;
} {
  const list = verdicts(report).filter((v): v is StepVerdict => v != null);
  if (list.length === 0) return { status: 'skip', note: 'هنوز چیزی اجرا نشده.' };

  const broken = list.find((v) => v.status === 'fail');
  if (broken) {
    return { status: 'fail', note: `${STEP_LABEL[broken.key]}: ${broken.note}` };
  }
  const shaky = list.find((v) => v.status === 'warn');
  if (shaky) {
    return { status: 'warn', note: `${STEP_LABEL[shaky.key]}: ${shaky.note}` };
  }
  const done = list.filter((v) => v.status === 'ok').length;
  return { status: 'ok', note: `${done} قدم از ${list.length} قدم درست کار کرد.` };
}

/**
 * گزارشی که این نسخه‌ی کد می‌فهمد، یا `null`.
 *
 * ردیفِ `queued` گزارشِ `{}` دارد و این حالتِ عادی است، نه خطا — پس این
 * تابع سکوت می‌کند و رابط «در صف» نشان می‌دهد.
 */
export function readReport(raw: unknown): SourceTestReport | null {
  if (!raw || typeof raw !== 'object') return null;
  const value = raw as Partial<SourceTestReport>;
  if (typeof value.version !== 'number') return null;
  if (!value.steps || typeof value.steps !== 'object') return null;
  return value as SourceTestReport;
}

// ═══════════ «این منبع چه چیزی لازم دارد؟» ═══════════

/**
 * ⚠ **مهم‌ترین تفکیکِ این بخش**: کاری که **کد** می‌خواهد از کاری که فقط
 * **تنظیم** می‌خواهد جدا است.
 *
 * بدونِ این تفکیک، فهرستِ منابعِ خراب یک تودهٔ بی‌شکل است. با آن، دو صفِ
 * متفاوت درمی‌آید: چند ده منبع که با یک کلیک درست می‌شوند، و چند تا که
 * واقعاً باید برایشان کد نوشت — و آن چند تا هم دسته‌بندی شده‌اند، پس یک
 * بارِ کد نوشتن می‌تواند ده تایشان را با هم حل کند.
 */
export type NeedKind =
  | 'none'      // چیزی لازم نیست
  | 'config'    // تنظیم، از همین رابط
  | 'code'      // توسعه در کرالر
  | 'external'; // مالِ سایتِ طرف است، نه ما

export type NeedKey =
  | 'ok' | 'off' | 'robots' | 'site-down' | 'bot-wall' | 'js-rendered'
  | 'wrong-url' | 'needs-parser' | 'needs-dates' | 'page2-parser'
  | 'pagination' | 'follow-detail' | 'wrong-extractor' | 'detail-only';

export interface SourceNeed {
  key: NeedKey;
  /** متنِ چیپ — کوتاه، چون کنارِ ردیفِ جدول می‌نشیند. */
  label: string;
  kind: NeedKind;
  /** شاهدی که این تشخیص را ساخت. */
  why: string;
  /** قدمِ بعدی، به‌زبانِ کاری که باید انجام شود. */
  todo: string;
}

/**
 * فشرده‌ی گزارش — همان چند عددی که تشخیص به آن‌ها نیاز دارد.
 *
 * ⚠ جدا از `SourceTestReport` است چون **دو مصرف‌کننده دارد**: پنلِ تست
 * که گزارشِ کامل را در دست دارد، و جدولِ منابع که فقط چند اسکالر از
 * آخرین اجرا را می‌گیرد. یک گزارشِ ۲۵۶ کیلوبایتی به‌ازای هر ردیفِ جدول
 * یعنی صفحه‌ای که باز نمی‌شود.
 */
export interface TestFacts {
  outcome: 'fetched' | 'blocked' | 'error' | null;
  bytes: number;
  http_status: number | null;
  bot_wall: string | null;
  robots_allowed: boolean | null;
  detail: string | null;

  items1: number | null;
  items2: number | null;
  fields1: number | null;
  fields2: number | null;
  /** آیا در نمونه‌ها اصلاً `starts_at` پیدا شد؟ */
  has_start: boolean;

  paginated: boolean;
  same_body: boolean | null;
  repeated: number | null;

  detail_url: boolean;
  detail_extra: number;
  /** صفحه‌ی رخداد به‌تنهایی عنوان و زمان داد؟ */
  detail_essential: boolean;
  /** خواننده‌ی بهتر، اگر مسابقه یکی پیدا کرده باشد. */
  best_extractor: string | null;
}

/** گزارشِ کامل → همان چند عدد. */
export function factsOf(report: SourceTestReport | null): TestFacts | null {
  const s = report?.steps;
  if (!s?.fetch) return null;
  const f = s.fetch.page1;
  const samples = s.extract?.samples ?? [];
  return {
    outcome: f.outcome,
    bytes: f.bytes,
    http_status: f.http_status,
    bot_wall: f.bot_wall,
    robots_allowed: f.robots_allowed,
    detail: f.detail,
    items1: s.detect?.page1_items ?? null,
    items2: s.detect?.page2_items ?? null,
    fields1: s.extract?.page1_with_fields ?? null,
    fields2: s.extract?.page2_with_fields ?? null,
    has_start: samples.some((x) => x.fields.some(
      (y) => y.field === 'starts_at' && y.value != null && y.value !== '')),
    paginated: s.paginate?.configured === true,
    same_body: s.paginate?.same_body ?? null,
    repeated: s.paginate?.repeated ?? null,
    detail_url: Boolean(s.detail?.url),
    detail_extra: s.detail?.extra_fields?.length ?? 0,
    detail_essential: s.detail?.essential === true,
    best_extractor: s.extract?.best ?? null,
  };
}

const NEED: Record<NeedKey, { label: string; kind: NeedKind; todo: string }> = {
  ok: {
    label: 'سالم', kind: 'none',
    todo: 'کاری لازم نیست.',
  },
  off: {
    label: 'خاموش', kind: 'config',
    todo: 'اگر می‌خواهی‌اش، روشنش کن؛ وگرنه ردش کن تا از صف بیرون برود.',
  },
  robots: {
    label: 'robots بست', kind: 'external',
    todo: 'سایت اجازه نداده. آدرسِ دیگری از همان سایت را امتحان کن، وگرنه ردش کن.',
  },
  'site-down': {
    label: 'سایت خطا داد', kind: 'external',
    todo: 'چند روز دیگر دوباره تست کن. اگر ماند، آدرس عوض شده یا سایت رفته.',
  },
  'bot-wall': {
    label: 'دیوارِ ضدربات', kind: 'code',
    todo: 'با fetcherِ فعلی نمی‌شود. یا کلیدِ API از سایت بگیر، یا این منبع '
      + 'منتظرِ مرورگرِ بی‌سر بماند.',
  },
  'js-rendered': {
    label: 'با جاوااسکریپت ساخته می‌شود', kind: 'code',
    todo: 'صفحه تقریباً خالی می‌آید و محتوا در مرورگر ساخته می‌شود. '
      + 'یا API پشتش را پیدا کن، یا منتظرِ مرورگرِ بی‌سر.',
  },
  'wrong-url': {
    label: 'آدرس اشتباه است', kind: 'config',
    todo: 'صفحه سالم است ولی فهرستِ رخداد نیست — احتمالاً وبلاگ یا صفحه‌ی '
      + 'معرفی. آدرسِ تقویمِ سایت را پیدا کن و در ویرایش بگذار.',
  },
  'wrong-extractor': {
    label: 'خواننده‌اش عوض شود', kind: 'config',
    todo: 'خواننده‌ی فعلی چیزی درنمی‌آورد ولی یکی دیگر می‌آورد. '
      + 'در پنلِ تست یک کلیک است.',
  },
  'detail-only': {
    label: 'از صفحه‌ی رخداد کار می‌کند', kind: 'none',
    todo: 'کارتِ فهرست کم‌فیلد است ولی صفحه‌ی خودِ رخداد عنوان و زمان '
      + 'می‌دهد — و خطِ لوله ادعاهای هر دو را با هم جمع می‌کند. این منبع '
      + 'قابلِ استفاده است؛ آماده‌ی ترفیع.',
  },
  'needs-parser': {
    label: 'پارسر لازم دارد', kind: 'code',
    todo: 'رخدادها روی صفحه هستند ولی هیچ ساختارِ استانداردی ندارند. '
      + '`cards` باید بهتر شود، یا این سایت پارسرِ خودش را لازم دارد.',
  },
  'needs-dates': {
    label: 'تاریخ درنمی‌آید', kind: 'code',
    todo: 'عنوان می‌آید ولی زمان نه — و بدونِ زمان رخداد ساخته نمی‌شود. '
      + 'خواندنِ تاریخِ متنی باید بهتر شود.',
  },
  'page2-parser': {
    label: 'صفحه‌ی دوم قالبش فرق دارد', kind: 'code',
    todo: 'صفحه‌ی اول درست است ولی صفحه‌ی دوم آیتم می‌دهد و فیلد نه. '
      + 'یعنی صفحه‌بندی هر شب دانلود می‌شود و هیچ رخدادی نمی‌دهد.',
  },
  pagination: {
    label: 'صفحه‌بندی خراب است', kind: 'config',
    todo: 'صفحه‌ی دوم همان اول است — قاعده‌ی `paginate` غلط است. '
      + 'الگوی آدرسِ صفحه‌ی دوم را از خودِ سایت بردار.',
  },
  'follow-detail': {
    label: 'صفحه‌ی جزئیات را روشن کن', kind: 'config',
    todo: 'کارتِ فهرست کم‌فیلد است ولی صفحه‌ی خودِ رخداد بیشتر می‌دهد. '
      + '`heuristic_detail` یا `follow` را روشن کن.',
  },
};

/**
 * تشخیصِ «چه چیزی لازم است» — **اولین** موردِ صادق، نه همه‌شان.
 *
 * ⚠ ترتیب همان ترتیبِ خطِ لوله است و دلیلش همان دلیلِ `overall`: وقتی
 * صفحه اصلاً نمی‌آید، هر پنج قدمِ بعدی هم خراب‌اند و برچسب زدنشان یک
 * مشکل را پنج تا نشان می‌دهد. اولین گلوگاه، تنها چیزی است که قدمِ بعدی
 * را می‌سازد.
 */
export function needOf(facts: TestFacts | null): SourceNeed | null {
  if (!facts) return null;
  const at = (key: NeedKey, why: string): SourceNeed => ({ key, why, ...NEED[key] });

  // ── ۱ ــ اصلاً نیامد ──
  if (facts.outcome === 'blocked') {
    if (facts.robots_allowed === false) return at('robots', 'robots.txt این آدرس را بست.');
    if (facts.detail === 'enabled=false') return at('off', 'منبع خاموش است.');
    return at('off', facts.detail ?? 'گرفتن پیش از درخواست متوقف شد.');
  }
  if (facts.outcome === 'error') {
    return at('site-down', `خطا${facts.http_status ? ` (HTTP ${facts.http_status})` : ''}.`);
  }
  if (facts.bot_wall) return at('bot-wall', `نشانه‌ی «${facts.bot_wall}» در صفحه.`);

  // ── ۲ ــ آمد، ولی چیزی در آن نبود ──
  //
  // ⚠ صفحه‌ی نازکِ بی‌آیتم با صفحه‌ی پرِ بی‌آیتم دو چیزِ متفاوت‌اند و دو
  // کارِ متفاوت می‌خواهند: اولی مرورگر لازم دارد، دومی فقط آدرسِ درست.
  if ((facts.items1 ?? 0) === 0) {
    return facts.bytes < THIN_PAGE_BYTES * 8
      ? at('js-rendered', `فقط ${facts.bytes} بایت و صفر آیتم.`)
      : at('wrong-url', `${facts.bytes} بایت آمد ولی هیچ آیتمی در آن نبود.`);
  }

  // ── ۳ ــ آیتم هست، فیلد نه ──
  //
  // ⚠️ **ترتیبِ این سه بند مهم است و هر سه از اشتباهاتِ واقعی آمده‌اند.**
  // «پارسر لازم دارد» گران‌ترین تشخیصِ این فهرست است — آدم را می‌فرستد
  // کد بنویسد. پس دو راهِ ارزان‌تر باید **قبلش** رد شوند.
  if ((facts.fields1 ?? 0) === 0) {
    // ۳الف ــ شاید فقط خواننده‌اش غلط تنظیم شده. هر پیش‌نویس با `jsonld`
    // ساخته می‌شود، پس این حالت نادر نیست — قاعده است.
    if (facts.best_extractor) {
      return at('wrong-extractor',
        `با «${facts.best_extractor}» فیلد درمی‌آید، با تنظیمِ فعلی نه.`);
    }
    // ۳ب ــ شاید کارت لازم نباشد. اگر صفحه‌ی خودِ رخداد عنوان و زمان
    // بدهد، منبع قابلِ استفاده است و هیچ کدی لازم ندارد.
    if (facts.detail_essential) {
      return at('detail-only', 'کارت فیلدی نداد ولی صفحه‌ی رخداد کامل بود.');
    }
    return at('needs-parser', `${facts.items1} آیتم پیدا شد و هیچ‌کدام فیلدی نداد.`);
  }
  if (!facts.has_start) {
    if (facts.best_extractor) {
      return at('wrong-extractor',
        `با «${facts.best_extractor}» زمان هم درمی‌آید، با تنظیمِ فعلی نه.`);
    }
    // همان استدلالِ بالا: زمانِ نبوده روی کارت، وقتی مهم نیست که صفحه‌ی
    // رخداد داشته باشدش.
    if (facts.detail_essential) {
      return at('detail-only', 'کارت زمان نداشت ولی صفحه‌ی رخداد داشت.');
    }
    return at('needs-dates', 'هیچ نمونه‌ای `starts_at` نداشت.');
  }

  // ── ۴ ــ صفحه‌ی اول درست است؛ حالا بقیه ──
  if (facts.paginated && facts.same_body === true) {
    return at('pagination', 'بدنه‌ی صفحه‌ی دوم مو‌به‌مو همان اول است.');
  }
  if (facts.paginated && (facts.items2 ?? 0) > 0 && facts.repeated === facts.items2) {
    return at('pagination', 'همه‌ی آیتم‌های صفحه‌ی دوم تکراری بودند.');
  }
  if (facts.paginated && (facts.items2 ?? 0) > 0 && facts.fields2 === 0) {
    return at('page2-parser', `صفحه‌ی دوم ${facts.items2} آیتم داد و صفر فیلد.`);
  }
  if (facts.detail_url && facts.detail_extra >= 3) {
    return at('follow-detail', `صفحه‌ی رخداد ${facts.detail_extra} فیلدِ بیشتر داشت.`);
  }

  return at('ok', `${facts.fields1} آیتم با فیلدِ کامل.`);
}

export const NEED_KIND_LABEL: Record<NeedKind, string> = {
  none: 'چیزی لازم نیست',
  config: 'تنظیم',
  code: 'توسعه در کرالر',
  external: 'مالِ سایتِ طرف',
};

/** ترتیبِ خواندن در فیلترها — از «کارِ ما» به «کارِ دیگران». */
export const NEED_ORDER: NeedKey[] = [
  'needs-parser', 'needs-dates', 'page2-parser', 'js-rendered', 'bot-wall',
  'wrong-extractor', 'wrong-url', 'pagination', 'follow-detail',
  'detail-only', 'off', 'robots', 'site-down', 'ok',
];

export const NEED_LABEL: Record<NeedKey, string> =
  Object.fromEntries(
    (Object.keys(NEED) as NeedKey[]).map((k) => [k, NEED[k].label]),
  ) as Record<NeedKey, string>;
